<template>
  <div class="cv-preview">
    <!-- Header Section -->
    <div v-if="cvData.personalInfo.fullName" class="cv-header">
      <h1 class="cv-name">
        {{ cvData.personalInfo.fullName }}
      </h1>
      
      <div v-if="contactParts.length" class="cv-contact">
        {{ contactParts.join(' | ') }}
      </div>
    </div>

    <!-- Education Section -->
    <div v-if="hasEducation" class="section-container-preview">
      <h2 class="section-header-preview">
        Education
      </h2>
      <hr class="section-divider-preview">

      <div v-for="(education, index) in validEducationBulletPoints" 
           :key="'edu-' + index" 
           class="entry-container">
        <table class="info-table">
          <tr>
            <td class="table-cell-title">
              {{ formatEducationFirstLine(education) }}
            </td>
            <td class="table-cell-date">
              {{ formatDateRange(education) }}
            </td>
          </tr>
          <tr>
            <td class="table-cell-subtitle">
              {{ formatEducationSecondLine(education) }}
            </td>
            <td class="table-cell-location">
              {{ education.location }}
            </td>
          </tr>
        </table>
        
        <ul v-if="education.bulletPoints.length" class="bullet-list">
          <li v-for="(point, pointIndex) in education.bulletPoints"
              :key="'edu-point-' + index + '-' + pointIndex"
              class="bullet-item">
            {{ point }}
          </li>
        </ul>
      </div>
    </div>

    <!-- Experience Section -->
    <div v-if="hasExperience" class="section-container-preview">
      <h2 class="section-header-preview">
        Work Experience
      </h2>
      <hr class="section-divider-preview">

      <div v-for="(experience, index) in validExperienceBulletPoints" 
           :key="'exp-' + index" 
           class="entry-container">
        <table class="info-table">
          <tr>
            <td class="table-cell-title">
              {{ formatExperienceTitle(experience) }}
            </td>
            <td class="table-cell-date">
              {{ formatDateRange(experience) }}
            </td>
          </tr>
          <tr>
            <td class="table-cell-subtitle">
              {{ experience.companyDescription }}
            </td>
            <td class="table-cell-location">
              {{ experience.location }}
            </td>
          </tr>
        </table>
        
        <ul v-if="experience.bulletPoints.length" class="bullet-list">
          <li v-for="(point, pointIndex) in experience.bulletPoints"
              :key="'exp-point-' + index + '-' + pointIndex"
              class="bullet-item">
            {{ point }}
          </li>
        </ul>
      </div>
    </div>

    <!-- Activities Section -->
    <div v-if="hasActivities" class="section-container-preview">
      <h2 class="section-header-preview">
        Extracurricular Activities
      </h2>
      <hr class="section-divider-preview">

      <div v-for="(activity, index) in validActivityBulletPoints" 
           :key="'act-' + index" 
           class="entry-container">
        <table class="info-table">
          <tr>
            <td class="table-cell-title">
              {{ formatActivityTitle(activity) }}
            </td>
            <td class="table-cell-date">
              {{ formatDateRange(activity) }}
            </td>
          </tr>
        </table>
        
        <ul v-if="activity.bulletPoints.length" class="bullet-list">
          <li v-for="(point, pointIndex) in activity.bulletPoints"
              :key="'act-point-' + index + '-' + pointIndex"
              class="bullet-item">
            {{ point }}
          </li>
        </ul>
      </div>
    </div>

    <!-- Skills & Interests Section -->
    <div v-if="hasSkillCategories" class="section-container-preview">
      <h2 class="section-header-preview">
        Skills & Interests
      </h2>
      <hr class="section-divider-preview">

      <div v-for="(category, index) in validSkillCategories" 
           :key="'skill-cat-' + index" 
           class="entry-container">
        <div class="skills-content">
          <span class="skills-category">{{ category.name }}</span>: {{ category.content }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed } from 'vue'
import '../styles/cv-preview.css'

export default {
  name: 'ClientSidePreview',
  props: {
    cvData: {
      type: Object,
      required: true,
      default: () => ({
        personalInfo: {},
        educationList: [],
        experienceList: [],
        activitiesList: [],
        skillCategories: []
      })
    }
  },
  setup(props) {
    // Computed properties
    const contactParts = computed(() => {
      const { personalInfo } = props.cvData
      const parts = []
      if (personalInfo?.phone) parts.push(personalInfo.phone)
      if (personalInfo?.email) parts.push(personalInfo.email)
      if (personalInfo?.location) parts.push(personalInfo.location)
      return parts
    })

    const hasEducation = computed(() => {
      return props.cvData.educationList?.some(edu => edu.institution)
    })

    const hasExperience = computed(() => {
      return props.cvData.experienceList?.some(exp => exp.jobTitle || exp.company)
    })

    const hasActivities = computed(() => {
      return props.cvData.activitiesList?.some(act => act.organization || act.role)
    })

    const hasSkillCategories = computed(() => {
      return props.cvData.skillCategories?.some(cat => cat.name && cat.content)
    })

    const validEducationBulletPoints = computed(() => {
      return props.cvData.educationList?.filter(edu => edu.institution).map(education => ({
        ...education,
        bulletPoints: education.bulletPoints?.filter(point => point?.trim()) || []
      })) || []
    })

    const validExperienceBulletPoints = computed(() => {
      return props.cvData.experienceList?.filter(exp => exp.jobTitle || exp.company).map(experience => ({
        ...experience,
        bulletPoints: experience.bulletPoints?.filter(point => point?.trim()) || []
      })) || []
    })

    const validActivityBulletPoints = computed(() => {
      return props.cvData.activitiesList?.filter(act => act.organization || act.role).map(activity => ({
        ...activity,
        bulletPoints: activity.bulletPoints?.filter(point => point?.trim()) || []
      })) || []
    })

    const validSkillCategories = computed(() => {
      return props.cvData.skillCategories?.filter(cat => cat.name && cat.content) || []
    })

    // Formatting methods
    const formatEducationFirstLine = (education) => {
      const parts = []
      if (education.programName) parts.push(education.programName)
      if (education.institution) parts.push(education.institution)
      return parts.join(' - ')
    }

    const formatEducationSecondLine = (education) => {
      const parts = []
      if (education.programType) parts.push(education.programType)
      if (education.gpa) parts.push(education.gpa)
      return parts.join(' - ')
    }

    const formatExperienceTitle = (experience) => {
      return [experience.jobTitle, experience.company].filter(Boolean).join(' - ')
    }

    const formatActivityTitle = (activity) => {
      return [activity.role, activity.organization].filter(Boolean).join(' - ')
    }

    const formatDateRange = (item) => {
      if (!item.startMonth && !item.startYear) return ''
      
      const start = item.startMonth && item.startYear 
        ? `${getMonthName(parseInt(item.startMonth))} ${item.startYear}`
        : (item.startYear || '')
        
      if (item.isCurrent) {
        return start ? `${start} - Present` : 'Present'
      }
      
      const end = item.endMonth && item.endYear
        ? `${getMonthName(parseInt(item.endMonth))} ${item.endYear}`
        : (item.endYear || '')
        
      if (!end) return start
      return start ? `${start} - ${end}` : end
    }

    const getMonthName = (monthNumber) => {
      const months = [
        'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
      ]
      return months[monthNumber - 1] || ''
    }

    return {
      contactParts,
      hasEducation,
      hasExperience,
      hasActivities,
      hasSkillCategories,
      validEducationBulletPoints,
      validExperienceBulletPoints,
      validActivityBulletPoints,
      validSkillCategories,
      formatEducationFirstLine,
      formatEducationSecondLine,
      formatExperienceTitle,
      formatActivityTitle,
      formatDateRange
    }
  }
}
</script>

<style>
/* Import global styles for CV preview */
@import '../styles/cv-preview.css';

/* Component-specific styles */
.cv-preview {
  min-height: 200px;
  width: 100%;
}

/* Ensure proper font rendering */
.cv-preview * {
  font-family: 'Georgia', serif !important;
}
</style>